import { useSyncExternalStore } from 'react';
import { devtools } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

// ---------------------------------------------------------------------------
// DEV-3456: the import list's multi-selection. Ephemeral — never persisted,
// never in the URL (the *active* row keeps living in `elementId`).
//
// This is a module-level store rather than component state because the row list
// and the conversion editor in ElementListHeader are siblings in ElementsView:
// there is no common React parent below the page to hold it.
// ---------------------------------------------------------------------------

export type ListSelectionState = {
  /** Candidate row ids, never element ids — a row keeps its id when it
   *  re-materializes, an element does not. */
  selectedIds: ReadonlySet<string>;
  /** Where a shift+click range measures from. */
  anchorId: string | undefined;
};

const EMPTY: ListSelectionState = {
  selectedIds: new Set(),
  anchorId: undefined,
};

const useStore = createWithEqualityFn<ListSelectionState>()(
  devtools(() => EMPTY, { name: 'list-selection' }),
  shallow,
);

export const useListSelection = (): ListSelectionState =>
  useStore((state) => state, shallow);

/** Imperative read for callbacks that run outside render. */
export const getListSelection = (): ListSelectionState => useStore.getState();

export const setListSelection = (next: ListSelectionState): void =>
  useStore.setState(next, true);

export const clearListSelection = (): void => useStore.setState(EMPTY, true);

const EMPTY_VISIBLE_IDS: ReadonlySet<string> = new Set();

export type VisibleListRowIdsSnapshot = {
  ids: ReadonlySet<string>;
  /** False until at least one import section has reported on-screen ids. */
  reported: boolean;
};

const visibleRowIdsByImport = new Map<string, ReadonlySet<string>>();
const visibleListeners = new Set<() => void>();
let visibleSnapshot: VisibleListRowIdsSnapshot = {
  ids: EMPTY_VISIBLE_IDS,
  reported: false,
};

const visibleSetsEqual = (
  a: ReadonlySet<string>,
  b: ReadonlySet<string>,
): boolean => {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
};

const emitVisibleListRowIds = (): void => {
  for (const listener of visibleListeners) listener();
};

const rebuildVisibleListRowIdsSnapshot = (): void => {
  const reported = visibleRowIdsByImport.size > 0;
  const nextIds = new Set<string>();
  for (const ids of visibleRowIdsByImport.values()) {
    for (const id of ids) nextIds.add(id);
  }
  const ids =
    nextIds.size === 0 ? EMPTY_VISIBLE_IDS : (nextIds as ReadonlySet<string>);
  if (
    visibleSnapshot.reported === reported &&
    visibleSetsEqual(visibleSnapshot.ids, ids)
  ) {
    return;
  }
  visibleSnapshot = { ids, reported };
  emitVisibleListRowIds();
};

/** Record the on-screen candidate ids for one import file. */
export const setVisibleListRowIds = (
  importId: string,
  ids: ReadonlySet<string>,
): void => {
  const prev = visibleRowIdsByImport.get(importId);
  if (prev && visibleSetsEqual(prev, ids)) return;
  visibleRowIdsByImport.set(importId, ids);
  rebuildVisibleListRowIdsSnapshot();
};

export const clearVisibleListRowIds = (importId: string): void => {
  if (!visibleRowIdsByImport.delete(importId)) return;
  rebuildVisibleListRowIdsSnapshot();
};

/** Union of on-screen candidate ids across import files. */
export const getVisibleListRowIds = (): ReadonlySet<string> =>
  visibleSnapshot.ids;

const subscribeVisibleListRowIds = (
  onStoreChange: () => void,
): (() => void) => {
  visibleListeners.add(onStoreChange);
  return () => {
    visibleListeners.delete(onStoreChange);
  };
};

export const getVisibleListRowIdsSnapshot = (): VisibleListRowIdsSnapshot =>
  visibleSnapshot;

/** On-screen candidate ids, for URL/nav readers that sit above the row list. */
export const useVisibleListRowIdsSnapshot = (): VisibleListRowIdsSnapshot =>
  useSyncExternalStore(
    subscribeVisibleListRowIds,
    getVisibleListRowIdsSnapshot,
    getVisibleListRowIdsSnapshot,
  );
